/* Shared bits for the public pages (match list, teams): team logo with initial fallback, player
 * avatar, FACEIT level from ELO, date formatting, map names. */
import React, { useState } from 'react';
import axios from 'axios';

// Public requests never send the admin token (same as the old pages).
export const publicApi = axios.create();
delete publicApi.defaults.headers.common['Authorization'];

const PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#ec4899', '#84cc16'];
export function teamColor(name) {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** Team logo: uploaded logo (avatar_v > 0), else an external logo_url, else the team's initial. */
export function TeamLogo({ id, name, avatarV, logoUrl, size = 32, className = '' }) {
  const [broken, setBroken] = useState(false);
  const src = !broken && ((id && avatarV) ? `/api/teams/${id}/avatar?v=${avatarV}` : (logoUrl || null));
  const style = { width: size, height: size };
  if (src) return <span className={`pp-logo ${className}`} style={style}><img src={src} alt="" loading="lazy" onError={() => setBroken(true)} /></span>;
  const c = teamColor(name);
  return (
    <span className={`pp-logo pp-logo-initial ${className}`} style={{ ...style, background: `${c}1f`, borderColor: `${c}55`, color: c, fontSize: Math.round(size * 0.42) }} aria-hidden="true">
      {String(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Player avatar from /api/avatars/<steam64>, silhouette fallback. avatar_v 0 = no avatar file on the
 *  panel (the endpoint would 404), so no request is made then. */
export function PlayerAvatar({ steamId, avatarV, name, size = 28, className = '' }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size };
  if (steamId && avatarV && !broken) {
    return <span className={`pp-avatar ${className}`} style={style} title={name || ''}><img src={`/api/avatars/${steamId}?v=${avatarV}`} alt="" loading="lazy" onError={() => setBroken(true)} /></span>;
  }
  return (
    <span className={`pp-avatar pp-avatar-empty ${className}`} style={style} title={name || ''}>
      <svg width={Math.round(size * 0.55)} height={Math.round(size * 0.55)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
    </span>
  );
}

/** FACEIT skill level (1–10) for an ELO — FACEIT's CS2 thresholds. */
export function faceitLevel(elo) {
  const e = Number(elo);
  if (!Number.isFinite(e) || e <= 0) return null;
  const caps = [500, 750, 900, 1050, 1200, 1350, 1530, 1750, 2000];
  const i = caps.findIndex(c => e <= c);
  return i === -1 ? 10 : i + 1;
}

export function FaceitElo({ elo, level, label }) {
  if (elo == null) return null;
  const lv = level || faceitLevel(elo);
  return (
    <span className="pp-elo" title={`FACEIT${lv ? ` level ${lv}` : ''} · ${elo} ELO`}>
      {lv && <img src={`/faceit-levels/skill_level_${lv}.png`} alt="" onError={e => { e.target.style.display = 'none'; }} />}
      {label && <span className="pp-elo-label">{label}</span>}
      <b>{elo}</b>
    </span>
  );
}

export function prettyMap(raw) {
  return String(raw || '').replace(/^(de|cs|ar|dz|gd)_/i, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Server DATETIME (UTC, "YYYY-MM-DD HH:MM:SS") → Date */
export function parseUtc(v) {
  if (!v) return null;
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? null : d;
}
export function fmtDay(v, lng) {
  const d = parseUtc(v);
  if (!d) return '';
  try { return d.toLocaleDateString(lng || undefined, { day: 'numeric', month: 'short' }); } catch (_) { return ''; }
}
export function fmtTime(v, lng) {
  const d = parseUtc(v);
  if (!d) return '';
  try { return d.toLocaleTimeString(lng || undefined, { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; }
}
